import {Injectable} from '@angular/core';
import Map from 'ol/Map';
import Draw, {createBox} from 'ol/interaction/Draw';
import GeoJSON from 'ol/format/GeoJSON.js';
import {GeoJSONFeature, GeoJSONFeatureCollection} from 'ol/format/GeoJSON';
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4';
import {Feature} from 'ol';
import VectorLayer from 'ol/layer/Vector';
import Polygon from 'ol/geom/Polygon';
import {getArea} from 'ol/sphere';
import {EventsService} from "./events.service";
import {centers, extents, layerStyle} from "../var/constants";
import {Type} from 'ol/geom/Geometry';
import View from "ol/View";
import {fromLonLat as transformLonLat} from "ol/proj";
import {nrwMask} from "../var/layers";


@Injectable({
  providedIn: 'root'
})
export class MapService {

  map: Map | undefined;
  geojson: GeoJSON;
  drawInteraction: any;

  region: 'brd' | 'nrw' = 'brd';

  constructor(
    private events: EventsService
  ) {
    this.geojson = new GeoJSON();
    proj4.defs('EPSG:25832', '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
    register(proj4);

    this.events.subscribe('product-changed', (data) => this.onProductChanged(data));
  }

  setMap(map: Map) {
    this.map = map;
  }

  getMap(): Map | undefined {
    return this.map;
  }

  getGeojson(): GeoJSONFeatureCollection | null {
    const vectorSource = this.getVectorLayer('drawLayer').getSource();
    const features = vectorSource?.getFeatures();
    if (features && features.length > 0) {
      const geom = features[0].getGeometry().clone().transform('EPSG:3857', 'EPSG:25832');
      const geoJSONFeature = JSON.parse(this.geojson.writeFeature(new Feature(geom))) as GeoJSONFeature;
      geoJSONFeature.properties = {};
      return {
        crs: {
          type: 'EPSG',
          properties: {
            code: 25832
          }
        },
        type: 'FeatureCollection',
        features: [geoJSONFeature]
      } as GeoJSONFeatureCollection;
    } else {
      return null;
    }
  }

  /**
   * Get vector layer by name
   * @param layerName name of the layer
   */
  getVectorLayer(layerName: string): VectorLayer<any> {
    let vLayer: any;
    this.getLayers().getArray().forEach((layer: any) => {
      if (layer instanceof VectorLayer) {
        if (layer.get('name') === layerName) {
          vLayer = layer;
        }
      }
    });
    return vLayer;
  }

  /**
   * Get all map layers
   */
  getLayers(): any {
    return this.map?.getLayers();
  }

  checkMinimumAreaSize() {
    const features = this.getVectorLayer('drawLayer').getSource()?.getFeatures();
    if (features && features.length > 0) {
      const polygon = features[0].getGeometry() as Polygon;
      // if (polygon.getArea() < 50e6) {
      // this.clearMap('drawLayer');
      this.events.publish('total-area:change', getArea(polygon));
      // }
    }
  }

  removeInteraction() {
    this.map?.removeInteraction(this.drawInteraction);
  }

  /**
   * Create new draw interaction and add it to the map
   * @param drawType  rectangle or polygon
   */
  setDrawInteraction(drawType: string): void {
    let geomType: Type;
    let geomFunction: any;
    // The actual draw interaction will be removed
    this.removeInteraction();
    // Get correct vectorlayer for drawing
    const vectorSource = this.getVectorLayer('drawLayer').getSource();
    // Multiple geometries are not allowed
    vectorSource?.clear();
    if (drawType === 'rectangle') {
      // Rectangle will be created with the help of the geomFunction createBox
      geomType = 'Circle';
      geomFunction = createBox();
    } else {
      geomType = 'Polygon';
      geomFunction = null;
    }

    if (vectorSource) {
      // Create new draw interaction and add it to the map
      this.drawInteraction = new Draw({
        type: geomType,
        source: vectorSource,
        geometryFunction: geomFunction,
        style: layerStyle
      });

      // After drawend the draw interaction can be removed
      this.drawInteraction.on('drawend', (evt: any) => {
        // The actual draw interaction will be removed on drawend
        this.removeInteraction();
      });
    }

    this.map?.addInteraction(this.drawInteraction);
  }

  clearMap(layerToClear?: string): void {
    this.events.publish('total-area:change', 0);
    if (layerToClear) {
      this.getVectorLayer(layerToClear).getSource()?.clear();
    } else {
      this.getVectorLayer('drawLayer').getSource()?.clear();
      this.getVectorLayer('regionsLayer').getSource()?.clear();
    }
  }

  onProductChanged(data: {productId: 'LB' | 'LB_NRW' | 'LB_V_ATKIS'}) {
    if (this.region !== 'brd' && data.productId === 'LB') {
      // set map extent to germany
      this.map?.setView(new View({
        center: transformLonLat(centers.brd),
        zoom: 6,
        extent: extents.brd
      }));
      // remove mask layer
      this.map?.removeLayer(nrwMask);
      this.region = 'brd';
    } else {
      if (this.region !== 'nrw') {
        // set map extent to germany
        this.map?.setView(new View({
          center: transformLonLat(centers.nrw),
          zoom: 6,
          extent: extents.nrw
        }));
        const layers = this.map?.getLayers();
        if (layers) {
          layers.insertAt(1, nrwMask);
        }
        this.region = 'nrw';
      }
    }
  }
}
